import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Search } from 'lucide-react';

import { Breadcrumb } from '../components/Breadcrumb';
import { apiService } from '../services/apiService';
import { getCachedData, setCachedData } from '../utils/pageCache';
import type { QuoteDetail, QuoteDetailLine } from '../services/types';
import { parseCalendarDate } from '../utils/calendarDate';

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const date = parseCalendarDate(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-CA');
}

function formatPrice(value: number, currency: string): string {
  const formatted = value.toFixed(2);
  const [dollars, cents] = formatted.split('.');
  const dollarsParts: string[] = [];
  let count = 0;
  for (let i = dollars.length - 1; i >= 0; i--) {
    if (count === 3) {
      dollarsParts.unshift(' ');
      count = 0;
    }
    dollarsParts.unshift(dollars[i]);
    count++;
  }
  return `${dollarsParts.join('')}.${cents} ${currency}`;
}

function getStatusBadge(
  quoteClosed: boolean,
  expired: boolean
): { text: string; className: string } {
  if (quoteClosed)
    return { text: 'Closed', className: 'bg-blue-100 text-blue-800' };
  if (expired) return { text: 'Expired', className: 'bg-red-100 text-red-800' };
  return { text: 'Open', className: 'bg-green-100 text-green-800' };
}

function filterNameFromAddress(address: string, name: string): string {
  if (!address || !name) return address || '';
  const lines = address.replace(/\\r\\n/g, '\n').split('\n');
  const filteredLines = lines.filter(
    (line) => line.trim().toLowerCase() !== name.trim().toLowerCase()
  );
  return filteredLines.join('\n');
}

export const QuoteTrackerPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quoteNum: quoteNumParam } = useParams<{ quoteNum: string }>();
  const [quoteDetail, setQuoteDetail] = useState<QuoteDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');

  const fetchQuote = useCallback(async (quoteNum: string) => {
    const quoteNumInt = parseInt(quoteNum, 10);
    if (isNaN(quoteNumInt)) {
      setError('Invalid quote number');
      setIsLoading(false);
      return;
    }

    const cacheKey = `quoteDetail_${quoteNum}`;
    const cached = getCachedData<QuoteDetail>(cacheKey);
    if (cached) {
      setQuoteDetail(cached);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getQuoteDetail(quoteNumInt);
      setQuoteDetail(data);
      setCachedData(cacheKey, data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch quote');
      setQuoteDetail(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (quoteNumParam) {
      setSearchValue(quoteNumParam);
      fetchQuote(quoteNumParam);
    }
  }, [quoteNumParam, fetchQuote]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/quotes/${encodeURIComponent(searchValue.trim())}`);
    }
  };

  const { header, lines } = quoteDetail || { header: null, lines: [] };
  const statusBadge = header
    ? getStatusBadge(header.quoteClosed, header.expired)
    : null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 w-full">
      <div className="w-full">
        <Breadcrumb currentPage={t('quoteTracker.title')} />

        {/* Header */}
        <div className="mb-8 mt-4 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('quoteTracker.title')}
            </h1>
            <p className="mt-2 text-gray-600">
              {t('quoteTracker.description')}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="space-y-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('quoteTracker.searchPlaceholder')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                disabled={isLoading}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={isLoading || !searchValue.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
            >
              {isLoading ? t('common.loading') : t('common.search', 'Search')}
            </button>
          </div>

          {/* Error */}
          {error && !isLoading && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-900">
                  {t('common.error')}
                </h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">{t('common.loading')}...</p>
            </div>
          )}

          {/* Initial State */}
          {!quoteDetail && !isLoading && !error && (
            <div className="text-center py-12 bg-blue-50 rounded-lg border border-blue-200">
              <Search className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <p className="text-blue-900 font-medium">
                {t('quoteTracker.enterQuoteNumber')}
              </p>
              <p className="text-blue-700 text-sm mt-1">
                {t('quoteTracker.searchInstructions')}
              </p>
            </div>
          )}

          {/* Quote Content */}
          {quoteDetail && header && !isLoading && (
            <>
              {/* Quote Header Section */}
              <div className="bg-white rounded border border-gray-300 shadow-sm">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-blue-900">
                      {t('quoteTracker.quoteHeader')}
                    </h2>
                    <p className="text-xs text-blue-700">
                      {header.customerName} ({header.btCustId || header.custId})
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadge?.className}`}
                    >
                      {statusBadge?.text}
                    </span>
                  </div>
                </div>

                <div className="p-4 space-y-6">
                  {/* Three Column Layout */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Ship To */}
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <h3 className="text-sm font-bold text-green-900 uppercase tracking-tight mb-3">
                        {t('quoteTracker.shipTo')}
                      </h3>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-900">
                          {header.shipToName || '—'}
                          {header.shipToNum && (
                            <span className="text-gray-600 font-normal">
                              {' '}
                              ({header.shipToNum})
                            </span>
                          )}
                        </p>
                        {header.shipToAddress && (
                          <p className="text-xs text-gray-700 whitespace-pre-line">
                            {filterNameFromAddress(
                              header.shipToAddress,
                              header.shipToName
                            )}
                          </p>
                        )}
                        {header.shipToContactName && (
                          <p className="text-xs text-gray-700">
                            <span className="font-medium">Contact:</span>{' '}
                            {header.shipToContactName}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Bill To */}
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                      <h3 className="text-sm font-bold text-purple-900 uppercase tracking-tight mb-3">
                        {t('quoteTracker.billTo')}
                      </h3>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-900">
                          {header.btCustName || header.customerName || '—'}
                          {header.btCustId && (
                            <span className="text-gray-600 font-normal">
                              {' '}
                              ({header.btCustId})
                            </span>
                          )}
                        </p>
                        {header.btAddress && (
                          <p className="text-xs text-gray-700 whitespace-pre-line">
                            {filterNameFromAddress(
                              header.btAddress,
                              header.btCustName || header.customerName
                            )}
                          </p>
                        )}
                        {header.btContactName && (
                          <p className="text-xs text-gray-700">
                            <span className="font-medium">Contact:</span>{' '}
                            {header.btContactName}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* General Info */}
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-tight mb-3">
                        {t('quoteTracker.generalInfo')}
                      </h3>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                        <div>
                          <span className="text-gray-600">
                            {t('quoteTracker.dateQuoted')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {formatDate(header.dateQuoted)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('quoteTracker.followUpDate')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {formatDate(header.followUpDate)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('quoteTracker.expirationDate')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {formatDate(header.expirationDate)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('quoteTracker.dueDate')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {formatDate(header.dueDate)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('quoteTracker.entryPerson')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {header.entryPerson || '—'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">
                            {t('quoteTracker.totalLines')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {lines.length}
                          </span>
                        </div>
                        <div className="col-span-2 pt-2 border-t border-gray-200 mt-2">
                          <span className="text-gray-600">
                            {t('quoteTracker.totalQuote')}:
                          </span>
                          <span className="ml-1 font-bold text-gray-900">
                            {formatPrice(
                              header.docTotalQuote,
                              header.currencyCode
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Internal Comments */}
                  {header.internalComment && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                        {t('quoteDashboard.detail.internalComment')}
                      </p>
                      <p className="text-sm text-gray-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
                        {header.internalComment}
                      </p>
                    </div>
                  )}

                  {/* Comments */}
                  {header.quoteComment && (
                    <div className="pt-4 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                        {t('quoteTracker.comment')}
                      </p>
                      <p className="text-sm text-gray-700">
                        {header.quoteComment}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Quote Lines Section */}
              <div className="bg-white rounded border border-gray-300 shadow-sm">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                  <h2 className="text-lg font-bold text-blue-900">
                    {t('quoteTracker.quoteLines')} ({lines.length})
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('quoteTracker.line')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('quoteTracker.partNum')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('quoteTracker.lineDescription')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('quoteTracker.qty')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('quoteTracker.basePrice')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('quoteTracker.discount')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-tight">
                          {t('quoteTracker.unitPrice')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line: QuoteDetailLine) => (
                        <tr
                          key={line.quoteLine}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="px-4 py-3 text-gray-900 font-medium">
                            {line.quoteLine}
                          </td>
                          <td className="px-4 py-3">
                            {line.partNum ? (
                              <Link
                                to={`/parts/${encodeURIComponent(line.partNum)}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                              >
                                {line.partNumUM || line.partNum}
                              </Link>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-700 max-w-xs truncate">
                            {line.lineDesc}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900">
                            {Math.round(line.sellingExpectedQty)} {line.ium}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900">
                            {formatPrice(
                              line.docExpUnitPrice,
                              header.currencyCode
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900">
                            {line.discountPercent
                              ? `${line.discountPercent}%`
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-gray-900">
                            {formatPrice(
                              line.docOrderBasedPrice,
                              header.currencyCode
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {lines.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                      {t('quoteTracker.noLines')}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
